/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject, Subject, ConnectableObservable, merge, combineLatest, of } from 'rxjs';
import {
	tap,
	map,
	pluck,
	filter,
	multicast,
	sample,
	takeUntil,
	distinctUntilChanged,
	switchMap,
	delay,
} from 'rxjs/operators';

import * as strings from '@touchplan/strings';

import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';

import { Point } from 'pixi.js-legacy';
import * as _ from 'lodash-es';

import { checkNestedOrNull, pad, setNested, essentiallyEquals } from 'utils/basic-utils';
import { calcAngle, getCenterOffsetPoint } from 'utils/ticket-utils';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';

import { FancyTicketAction } from 'src/app/app-data/actions/ticket-actions';
import { Ticket, Side, LinkMap } from '@touchplan/common';
import { Line } from 'src/app/app-data/models/Line';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { ListLike } from 'src/app/app-data/interfaces/list-like';

import { MILESTONE_SIZE_OFFSET2 } from '@appPlanApplication/../../plain-javascript/canvas-common/interfaces/milestone-size.interface';

import {
	DependencyModeTypes,
	DependencyKeys,
	DependencyMode,
	DependencyDisplay,
} from './DependencyInterfaces';
import { DependencyLinkageType } from 'src/app/app-data/models/DependencyLinkageType';
export * from './DependencyInterfaces';

export type OutputLinks = LinkMap<[Ticket, Ticket], string, string>;

// FIXME: At some point, prepend an "I" to the following interface names
// tslint:disable: interface-name

interface DependencyData {
	isCyclic: boolean;
	isOutOfOrder: boolean;
	linkageType: DependencyLinkageType;
	lagDays: number;
}

interface DependencyLink {
	id: string;
	line?: Line;
	dependencyData?: DependencyData;
	lastData?: any;
	ticket?: Ticket;
}

interface LastTicketData {
	predecessors: any;
	successors: any;
	top: number;
	left: number;
	width: number;
	height: number;
}

interface TicketCache {
	ticket: Ticket;
	prevData?: LastTicketData;
}
interface DelayedInitialization {
	ticket: Ticket;
	predData: DependencyData;
}

interface PlannedFancyTicketAction extends FancyTicketAction {
	planId?: string;
}

export interface IPredecessorData {
	[key: string]: { [key: string]: DependencyData | null };
}

interface DependencyEditOuput {
	predecessors: IPredecessorData;
}

type DependencyTree = Map<string, Map<string, DependencyLink>>;

export const defaultLinkageType = DependencyLinkageType.FinishToStart;
export const defaultLagDays = 0;

export const defaultDependencyData: DependencyData = {
	isCyclic: false,
	isOutOfOrder: false,
	linkageType: defaultLinkageType,
	lagDays: defaultLagDays,
};
const errorLineColor = 0xc32a3c;
const greyLineColor = 0xcccccc;

export class Dependencies {
	//map vs object... hard to say
	// start with maps?
	private predecessorTree: DependencyTree = new Map();
	private successorTree: DependencyTree = new Map();
	private destroyer$ = new Subject();

	private ticketCache: Map<string, TicketCache> = new Map();

	public get ticketCacheSize() {
		return this.ticketCache.size;
	}
	/** The size including same plan timeline duplicates */
	public get totalTicketCount() {
		return this.timelineTickets.syncList.size + this.tickets.syncList.size;
	}
	/**
	 * The tickets that need lines drawn to tickets that haven't yet loaded.
	 * Indexed by the ticket it's waiting for.
	 * The value however is for the ticket doing the waiting.
	 */
	private waitingForInitialization: Map<string, DelayedInitialization> = new Map();

	public mode = new BehaviorSubject<DependencyMode>({ type: DependencyModeTypes.all, isAllType: true });
	public syncMode: DependencyMode;

	private preEditAllSetting = true;

	private editTimerId;

	public line$: ConnectableObservable<Line[]>;

	constructor(
		private tickets: TicketList,
		private timelineTickets: TicketList,
		public currentPlanId: string
	) {
		this.setup();
	}

	public setup() {
		const base$ = merge(
			this.tickets.rawEvent$.pipe(
				map((action) => {
					(action as PlannedFancyTicketAction).planId = this.currentPlanId;
					return action;
				})
			),
			this.timelineTickets.rawEvent$
		).pipe(
			//stifle that infinite loop
			// filter(action => action.type !== TicketActionTypes.updateDependencyTarget),
			filter((action) => {
				return this.tickets.isRemoveAction(action) || this.hasDataChanged(action.payload);
			}),
			//use the tap to build up the cache
			tap((action: PlannedFancyTicketAction) => {
				// if(action.key === "-Lq7swMjogwKnqMCtUry,-Lq7sz2RGiam756ftSHg" || action.key === "-Lq7t22Zr6e4xKA0BXOh"){ debugger; }
				// console.timeEnd('dep test');
				// logger.log('dependency thing', action);
				//special handling for timeline tickets in the same plan as the current one
				//single line version
				if (
					action.payload.isTimelineTicket &&
					action.payload.timelineTicketSplitId[0] === this.currentPlanId
				) {
					return;
				} //skip this shit
				// logger.log(action.payload.view.activityName, action.key);

				if (this.tickets.isRemoveAction(action)) {
					//restore the "predecessor awaiting a successor state", mainly related to timeline tickets
					if (this.successorTree.has(action.key)) {
						this.successorTree.get(action.key).forEach((predLink) => {
							const predData = this.predecessorTree.get(predLink.id).get(action.key);
							this.waitingForInitialization.set(action.key, {
								ticket: predLink.ticket,
								predData: predData.dependencyData,
							});
						});
					}
					if (action.key === this.syncMode.target) {
						this.setModeToOff();
					}
					this.removeWholeList(action.key, this.predecessorTree, this.successorTree);
					this.removeWholeList(action.key, this.successorTree, this.predecessorTree);
				}
				//adds and updates shouldn't be notably different
				else {
					this.ticketCache.set(action.key, { ticket: action.payload });

					if (action.payload.view.predecessors) {
						if (!this.predecessorTree.has(action.key)) {
							this.predecessorTree.set(action.key, new Map());
						}
						//add stuff
						for (const planId in action.payload.view.predecessors) {
							//skip timeline tickets referring to different plans MOC-3134
							if (
								this.specialIsTimelineTicketCheck(action.payload) &&
								planId !== this.currentPlanId &&
								!this.tickets.syncList.has(action.key)
							) {
								continue;
							}

							// tslint:disable-next-line: forin
							for (const ticketId in action.payload.view.predecessors[planId]) {
								let revisedId;
								const otherPlanId = planId + ',' + ticketId;

								if (planId === this.currentPlanId) {
									revisedId = ticketId;
								} else {
									revisedId = otherPlanId;
								}

								const predData = action.payload.view.predecessors[planId][ticketId];
								//connect things
								if (this.ticketCache.has(revisedId)) {
									const endTicket = this.ticketCache.get(revisedId).ticket;
									this.populateLink(action.payload, endTicket, predData);
								} else {
									// this.waitingForInitialization.set(ticketId, {ticket: action.payload, predData: predData});
									this.waitingForInitialization.set(revisedId, { ticket: action.payload, predData });
								}
							}
						}
					}
					//fix up loading order things
					if (this.waitingForInitialization.has(action.key)) {
						const delayed = this.waitingForInitialization.get(action.key);
						if (
							!this.predecessorTree.has(delayed.ticket.$id) ||
							!this.predecessorTree.get(delayed.ticket.$id).has(action.key)
						) {
							this.populateLink(delayed.ticket, action.payload, delayed.predData);
							this.waitingForInitialization.delete(action.key);
						}
					}
					//remove Stuff
					if (this.predecessorTree.has(action.key)) {
						this.predecessorTree.get(action.key).forEach((t, successorId, theMap) => {
							const successorTuple = this.getPlanFromCombinedId(successorId);
							if (
								!checkNestedOrNull(action.payload.view, 'predecessors', successorTuple[0], successorTuple[1])
							) {
								theMap.delete(successorId);
								//now remove the successor
								const tree = this.successorTree.get(successorId);
								if (tree) {
									tree.delete(action.key);
								}
							}
						});
					}
				}
			}),
			// filter(action => this.predecessorTree.has(action.key)), //TODO - this is probably incorrect, consider this a placeholder?
			map((action) => {
				const srcTicket = action.payload;
				this.setLine(srcTicket, action.key);

				return this.predecessorTree;
			})
		);

		//TODO - the mode and depenency graphs can now be combined and converted into the line list
		//TODO - the calculating which things are implicated and the lines should probably be isolated
		//and will need to be for the borders. Still haven't decided on the best method...
		const lineList$ = combineLatest([base$, this.mode]).pipe(
			takeUntil(this.destroyer$),
			sample(renderQueue.ob$.pipe(takeUntil(this.destroyer$))),
			//we'll start by just building an array, may want to switch to a map?
			map((combination) => {
				const mode = combination[1];
				const arr = [];
				//don't redraw all lines that are standard
				// let standardsInUse = {};

				if (mode.type === DependencyModeTypes.hidden) {
					return arr;
				}
				let predLinks: LinkMap;
				let succLinks: LinkMap;
				if (mode.type === DependencyModeTypes.standard || mode.type === DependencyModeTypes.allStandard) {
					predLinks = slideDownTree(mode.target, this.predecessorTree).found;
					predLinks.forEachy((predKey, succKey) => {
						const pred = this.predecessorTree.get(predKey).get(succKey);
						if (pred.ticket.hidden) {
							return;
						}
						arr.push(pred.line);
					});

					succLinks = slideDownTree(mode.target, this.successorTree).found;
					succLinks.forEachy((predKey, succKey) => {
						const pred = this.predecessorTree.get(predKey).get(succKey);
						if (pred.ticket.hidden) {
							return;
						}
						arr.push(pred.line);
					}, true);
				}

				if (
					mode.type === DependencyModeTypes.allStandard ||
					mode.type === DependencyModeTypes.all ||
					mode.type === DependencyModeTypes.allSpecial
				) {
					combination[0].forEach((subMap, startTicketId) => {
						subMap.forEach((end, endTicketId) => {
							const start = this.ticketCache.get(startTicketId);
							let line;
							if (
								(predLinks && predLinks.has(startTicketId, endTicketId)) ||
								(succLinks && succLinks.has(endTicketId, startTicketId))
							) {
								line = end.line;
							}
							// if(startTicketId === mode.target || endTicketId === mode.target){
							// 	line = end.line;
							// }
							else {
								//render all colors
								// if(mode.type === DependencyModeTypes.allSpecial){
								if (mode.type === DependencyModeTypes.allSpecial || end.line.color === errorLineColor) {
									line = end.line;
								}
								//this keeps the original data stable, but comes at a garbage collection cost
								else {
									line = Line.clone(end.line);
									line.color = greyLineColor;
								}
								if (!end.ticket.hidden && !start.ticket.hidden) {
									arr.push(line);
								}
							}
						});
					});
				}

				return arr;
			})
		);

		this.line$ = lineList$.pipe(multicast(() => new Subject())) as ConnectableObservable<Line[]>;
		this.line$.connect();

		this.mode.pipe(takeUntil(this.destroyer$)).subscribe((mode) => {
			//ignore the first tick. This will be a problem if we want to init the app with a target
			if (!this.syncMode) {
				this.syncMode = mode;
				return;
			}

			// if(mode.target !== this.syncMode.target){
			// 	if(this.syncMode.target){
			// 		let combined = this.getPlanFromCombinedId(this.syncMode.target);
			// 		let id = combined[0] === this.currentPlanId ? combined[1] : this.syncMode.target;
			//
			// 		let ticket = this.ticketCache.get(id);
			// 		if(!ticket){console.error("ticket is undefined in ticketCache, dependencies updated too slow.");}
			// 		let ticketList = ticket.ticket.isTimelineTicket ? this.timelineTickets : this.tickets;
			// 		ticketList.appEvent$.next({
			// 			key: id,
			// 			payload : ticket,
			// 			type: TicketActionTypes.updateDependencyTarget,
			// 			weightyness: undefined
			// 		} as any);
			// 	}
			// 	if(mode.target){
			// 		let combined = this.getPlanFromCombinedId(mode.target);
			// 		let id = combined[0] === this.currentPlanId ? combined[1] : mode.target;
			// 		let ticket = this.ticketCache.get(id);
			// 		if(!ticket){console.error("ticket is undefined in ticketCache, dependencies updated too slow.");}
			// 		let ticketList = ticket.ticket.isTimelineTicket ? this.timelineTickets : this.tickets;
			// 		ticketList.appEvent$.next({
			// 			key: id,
			// 			payload : ticket,
			// 			type: TicketActionTypes.updateDependencyTarget,
			// 			weightyness: DependencyWeight.primary
			// 		} as any);
			// 	}
			// }
			this.syncMode = mode;
		});

		//edit watcher
		//only check tickets
		this.tickets.editTicket$
			.pipe(
				map((list) => {
					return { count: list.size, list };
				}),
				//only changed if it's changing to/ from 0
				distinctUntilChanged((a, b) => a.count === b.count || (a.count > 0 && b.count > 0)),
				pluck('list'),
				//give the ticketCache time to update in the case of duplicating and immediately opening it in edit
				switchMap((list) => of(list).pipe(delay(100))),

				takeUntil(this.destroyer$)
			)
			.subscribe((list: Map<string, Ticket>) => {
				if (list.size) {
					const first = list.keys().next().value;
					this.toggleTarget(first);
					this.preEditAllSetting = this.syncMode.isAllType;
					this.toggleAll(true);
				} else {
					this.toggleTarget();
					this.toggleAll(this.preEditAllSetting);
				}
			});
	}

	/**
	 * Returns true if a ticket is being edited and if the given ticket is a predecessor of that edited ticket
	 */
	public hasPredecessor(predTicket: Ticket): boolean {
		const linkedMap = this.getLinks([predTicket]);
		let match = false;
		// Checking the entire edit list instead of just the first item,
		// like the rest of the class, where it's in "syncMode.target".
		this.tickets.editTicket$.getValue().forEach((ticket) => {
			if (match) {
				return;
			}
			match = linkedMap.has(ticket.$id, predTicket.$id);
		});
		return match;
	}

	private populateLink(srcTicket: Ticket, targetTicket: Ticket, predData: DependencyData) {
		if (!this.predecessorTree.has(srcTicket.$id)) {
			this.predecessorTree.set(srcTicket.$id, new Map());
		}
		this.predecessorTree.get(srcTicket.$id).set(targetTicket.$id, this.makeLink(targetTicket, predData));

		if (!this.successorTree.has(targetTicket.$id)) {
			this.successorTree.set(targetTicket.$id, new Map());
		}
		this.successorTree.get(targetTicket.$id).set(srcTicket.$id, this.makeLink(srcTicket));
	}

	/**
	 * yet more special handling for same plan timeline tickets. Pulls a ticket out
	 * of the cache accounting for it having a timeline ticket style id
	 */
	public checkTicketCache(ticketId: string) {
		const splitId = ticketId.split(',');
		if (splitId.length > 1 && splitId[0] === this.currentPlanId) {
			ticketId = splitId[1];
		}

		const cachey = this.ticketCache.get(ticketId);
		return cachey ? cachey.ticket : null;
	}

	private setMode(type: DependencyModeTypes, target?: string, depth?: number) {
		const allTypes = {
			[DependencyModeTypes.all]: DependencyModeTypes.all,
			[DependencyModeTypes.allSpecial]: DependencyModeTypes.allSpecial,
			[DependencyModeTypes.allStandard]: DependencyModeTypes.allStandard,
		};
		this.mode.next({
			type,
			isAllType: !!allTypes[type],
			target,
			depth,
		});
	}
	public setModeToAll() {
		this.setMode(DependencyModeTypes.all);
	}
	public setModeToStandard(ticketId: string) {
		this.setMode(DependencyModeTypes.standard, ticketId);
	}
	public setModeToAllStandard(ticketId: string) {
		this.setMode(DependencyModeTypes.allStandard, ticketId);
	}
	public setModeToOff() {
		this.setMode(DependencyModeTypes.hidden);
	}
	public toggleTarget(target?: string) {
		let turnOn = false;
		if (target !== this.syncMode.target) {
			turnOn = true;
		}
		analyticsEventsService.selectedDependencyTarget(!!this.syncMode.target, target, this.currentPlanId);
		if (this.syncMode.isAllType) {
			this.setMode(
				turnOn ? DependencyModeTypes.allStandard : DependencyModeTypes.all,
				turnOn ? target : undefined
			);
		} else {
			this.setMode(
				turnOn ? DependencyModeTypes.standard : DependencyModeTypes.hidden,
				turnOn ? target : undefined
			);
		}
	}
	public toggleAll(force?: boolean) {
		const turnOn = force !== undefined ? force : !this.syncMode.isAllType;

		if (this.syncMode.isAllType === turnOn) {
			return;
		} // suppress duplicates
		analyticsEventsService.toggledShowAllDependencies(turnOn);
		if (this.syncMode.target) {
			this.setMode(
				turnOn ? DependencyModeTypes.allStandard : DependencyModeTypes.standard,
				this.syncMode.target
			);
		} else {
			this.setMode(turnOn ? DependencyModeTypes.all : DependencyModeTypes.hidden);
		}
	}

	private setLine(ticket: Ticket, ticketId: string) {
		setLineForTree(ticket, ticketId, this.predecessorTree);
		if (this.successorTree.has(ticketId)) {
			this.successorTree.get(ticketId).forEach((thing, otherTicketId) => {
				setLineForTree(thing.ticket, otherTicketId, this.predecessorTree);
			});
		}
		// setLineForTree(ticket, ticketId, this.successorTree);
	}

	//TODO - this changed check may be better served by existing on the Ticket model
	//TODO - include a changed list in actions for the "local" events, assume a "full"
	//TODO - update for database events?
	private hasDataChanged(ticket: Ticket) {
		if (!this.ticketCache.has(ticket.$id)) {
			return true;
		}
		const prev = this.ticketCache.get(ticket.$id);
		//simple stuff
		return (
			['left', 'top', 'width', 'height'].some((key) => prev[key] !== ticket.view[key]) ||
			this.hasCessorDataChanged(ticket, prev.ticket)
		);
	}

	//TODO - implement
	private hasCessorDataChanged(ticket: Ticket, prev: Ticket) {
		if (
			(ticket.view.predecessors && !prev.view.predecessors) ||
			(!ticket.view.predecessors && prev.view.predecessors)
		) {
			return true;
		}
		//all else fails, deep equality check
		return _.isEqual(ticket.view.predecessors, prev.view.predecessors);
	}

	/**
	 * Make a link
	 * @param  endTicket   The ticket that is being referenced, this.predecessorTree[startTicket] -> endTicket
	 * @return             [description]
	 */
	private makeLink(endTicket: Ticket, dependencyData?: DependencyData) {
		return {
			id: endTicket.$id,
			ticket: endTicket,
			dependencyData: { ...defaultDependencyData, ...dependencyData },
		};
	}

	private removeWholeList(mainListKey: string, mainList: DependencyTree, referencedList: DependencyTree) {
		const main = mainList.get(mainListKey);
		if (!main) {
			return;
		} //removed thing that has no dependencies
		main.forEach((val, key) => {
			const opposite = referencedList.get(key);
			if (opposite) {
				opposite.delete(mainListKey);
				if (opposite.size === 0) {
					referencedList.delete(key);
				}
			} else {
				//broken dependency
			}
		});
		mainList.delete(mainListKey);
	}

	/**
	 * Returns a tuple, first arguement is the planId, the second is the ticketId
	 * TODO - most likely this should be replaced with a cached planId value
	 */
	private getPlanFromCombinedId(combinedId: string) {
		const brokenUp = combinedId.split(',');
		if (brokenUp.length === 2) {
			return brokenUp;
		} else {
			return [this.currentPlanId, combinedId];
		}
	}

	/** debug thing */
	private dumpTree() {
		function build(tree) {
			const obj = [];
			let maxLength = 0;
			tree.forEach((deeperTree, firstKey) => {
				if (firstKey.length > maxLength) {
					maxLength = firstKey.length;
				}
			});

			tree.forEach((deeperTree, firstKey) => {
				deeperTree.forEach((val, key) => {
					obj.push(pad(firstKey, maxLength, ' ') + ' -> ' + key);
				});
			});
			return obj;
		}

		return JSON.stringify(
			{
				predecessors: build(this.predecessorTree),
				successors: build(this.successorTree),
			},
			null,
			2
		);
	}

	//-------------------------------------------
	//- Writes
	//-------------------------------------------

	private addPredecessor(
		targetTicketId: string,
		srcTicketId: string,
		linkageType: DependencyLinkageType,
		lagDays: number
	): DependencyEditOuput | null {
		if (!srcTicketId) {
			srcTicketId = this.syncMode.target;
		}
		if (!this.writeValidation(targetTicketId, srcTicketId)) {
			return null;
		}

		const output = this._addPredecessor(targetTicketId, srcTicketId, linkageType, lagDays);
		return output[srcTicketId];
	}

	private _addPredecessor(
		targetTicketId: string,
		srcTicketId: string,
		linkageType: DependencyLinkageType,
		lagDays: number
	) {
		const currentMap = this.getTicketPredecessorLinks(srcTicketId);
		const depData = this.calculateLocalDependencyData(
			this.timelineSamePlanSafeCacheGet(targetTicketId).ticket,
			this.timelineSamePlanSafeCacheGet(srcTicketId).ticket,
			linkageType,
			lagDays
		);
		currentMap.set(srcTicketId, targetTicketId, depData);

		const dirtyOut = this.createOutput(currentMap);
		dirtyOut[srcTicketId].predecessors = this.decorateTimelineOutput(
			dirtyOut[srcTicketId].predecessors,
			this.ticketCache.get(srcTicketId).ticket,
			'predecessors'
		);

		const out = this.cleanupOutput(dirtyOut, srcTicketId);
		return out;
	}

	public removePredecessor(targetTicketId: string, srcTicketId?: string): DependencyEditOuput | null {
		if (!srcTicketId) {
			srcTicketId = this.syncMode.target;
		}
		if (!this.writeValidation(targetTicketId, srcTicketId)) {
			return null;
		}

		const output = this._removePredecessor(targetTicketId, srcTicketId);

		return output[srcTicketId];
	}

	private _removePredecessor(targetTicketId: string, srcTicketId: string) {
		const currentMap = this.getTicketPredecessorLinks(srcTicketId);

		//normal remove
		currentMap.set(srcTicketId, targetTicketId, null);

		//special remove for timeline tickets on the same plan
		// only do this with the target initially since it's the one with the problem
		// it might be become a problem with src as well
		const combTarget = this.getPlanFromCombinedId(targetTicketId);
		if (combTarget[1] === targetTicketId) {
			//tapped plan ticket
			currentMap.set(srcTicketId, combTarget.join(','), null);
		} else {
			//tapped timeline ticket
			currentMap.set(srcTicketId, combTarget[1], null);
		}
		//end special remove

		//don't love this, might be better to change the output function
		const dirtyOut = this.createOutput(currentMap);
		dirtyOut[srcTicketId].predecessors = this.decorateTimelineOutput(
			dirtyOut[srcTicketId].predecessors,
			this.ticketCache.get(srcTicketId).ticket,
			'predecessors'
		);
		const out = this.cleanupOutput(dirtyOut, srcTicketId);
		return out;
	}

	//TODO - this is much less necessary now, trim or remove
	private cleanupOutput(dirtyOut, ticketId: string, keyOverride = 'predecessors') {
		let out;
		if (dirtyOut[ticketId]) {
			out = dirtyOut;
		} else {
			out = { [ticketId]: { [keyOverride]: dirtyOut } };
		}
		return out;
	}

	public getDependencyLink(targetTicketId: string): DependencyLink {
		const srcTicketId = this.syncMode.target;
		const srcTicket = this.timelineSamePlanSafeCacheGet(srcTicketId);
		const targetTicket = this.timelineSamePlanSafeCacheGet(targetTicketId);
		if (
			this.predecessorTree.has(srcTicket.ticket.$id) &&
			this.predecessorTree.get(srcTicket.ticket.$id).has(targetTicket.ticket.$id)
		) {
			return this.predecessorTree.get(srcTicket.ticket.$id).get(targetTicket.ticket.$id);
		}
		return null;
	}

	public getDependencyData(targetTicketId: string): DependencyData {
		const resultLink = this.getDependencyLink(targetTicketId);
		return resultLink ? resultLink.dependencyData : null;
	}

	public togglePredecessor(targetTicketId: string, linkageType: DependencyLinkageType, lagDays: number) {
		const srcTicketId = this.syncMode.target;
		const srcTicket = this.timelineSamePlanSafeCacheGet(srcTicketId);
		const targetTicket = this.timelineSamePlanSafeCacheGet(targetTicketId);

		const resultLink = this.getDependencyLink(targetTicketId);
		if (!!resultLink) {
			return this.removePredecessor(targetTicket.ticket.$id, srcTicket.ticket.$id);
		} else {
			return this.addPredecessor(targetTicket.ticket.$id, srcTicket.ticket.$id, linkageType, lagDays);
		}
	}

	private calculateLocalDependencyData(
		targetTicket: Ticket,
		srcTicket: Ticket,
		linkageType: DependencyLinkageType,
		lagDays: number
	) {
		// tslint:disable-next-line: whitespace
		const depData = Object.assign({}, defaultDependencyData);
		depData.linkageType = linkageType;
		depData.lagDays = lagDays;

		if (targetTicket.view.liveFinish > srcTicket.view.liveStart) {
			depData.isOutOfOrder = true;
		}

		const newTree = copyDependencyTree(this.predecessorTree);
		if (!newTree.has(srcTicket.$id)) {
			newTree.set(srcTicket.$id, new Map());
		}
		newTree.get(srcTicket.$id).set(targetTicket.$id, { id: targetTicket.$id });

		depData.isCyclic = slideDownTree(srcTicket.$id, newTree).isCyclical;
		return depData;
	}

	/** yet more special things to handle same plan timeline tickets */
	private timelineSamePlanSafeCacheGet(ticketId: string) {
		let ticket = this.ticketCache.get(ticketId);
		if (!ticket) {
			const splitId = ticketId.split(',');
			if (splitId.length > 1 && splitId[0] === this.currentPlanId) {
				ticket = this.ticketCache.get(splitId[1]);
			}
		}
		return ticket;
	}

	/** returns false if validation has failed */
	private writeValidation(targetTicketId: string, srcTicketId: string) {
		let errString;
		let blockSilent = false;
		const targetTicket = this.timelineSamePlanSafeCacheGet(targetTicketId);
		const srcTicket = this.timelineSamePlanSafeCacheGet(srcTicketId);

		if (targetTicketId === srcTicketId) {
			blockSilent = true;
		}
		//MOC-3771
		// else if(targetTicket.ticket.view.isActualized){
		// 	errString = strings.SET_DEPENDENCY_TO_ACTUAL(srcTicket.ticket.view.activityName);
		// }
		else if (srcTicket.ticket.view.isActualized) {
			errString = strings.SET_DEPENDENCY_FROM_ACTUAL;
		} else if (
			this.predecessorTree.has(targetTicketId) &&
			this.predecessorTree.get(targetTicketId).has(srcTicketId)
		) {
			errString = strings.SET_DEPENDENCY_TO_OPPOSITE(targetTicket.ticket.view.activityName);
		}

		if (errString) {
			Logging.warning(errString);
		}
		// logger.log('blockSilent', !blockSilent, !errString)
		return !blockSilent && !errString;
	}

	private getTicketPredecessorLinks(ticketId: string) {
		const links = new LinkMap<DependencyData>();
		const tree = this.predecessorTree.get(ticketId);
		if (tree) {
			tree.forEach((dep, targetKey) => {
				links.set(ticketId, targetKey, dep.dependencyData);
			});
		}
		return links;
	}

	private getTicketLinks(ticketId: string) {
		const links = new LinkMap<DependencyData>();
		const tree = this.predecessorTree.get(ticketId);
		if (tree) {
			tree.forEach((dep, targetKey) => {
				links.set(ticketId, targetKey, dep.dependencyData);
			});
		}

		//refactor this
		const succTree = this.successorTree.get(ticketId);
		if (succTree) {
			succTree.forEach((dep, targetKey) => {
				this.predecessorTree.get(targetKey).forEach((dep2, targetKey2) => {
					links.set(targetKey, targetKey2, dep.dependencyData);
				});
			});
		}

		return links;
	}

	public destroy() {
		this.destroyer$.next(true);
		this.mode.complete();
		this.mode = null;
		this.ticketCache = null;
		this.predecessorTree = null;
		this.successorTree = null;
		this.waitingForInitialization = null;
		this.tickets = null;
		this.timelineTickets = null;
	}

	//writes: "srcId/predecessors/targetPlanId/targetTicketId"
	// returns an empty object when there's nothing to do
	private createOutput(dependencies: LinkMap<DependencyData>) {
		const writeTree = DependencyKeys.predecessors;
		const output: any = {};

		dependencies.forEachy((src, target, depData) => {
			const morphedTarget = this.getPlanFromCombinedId(target);

			//can calculate client side written isCyclic and isOutOfOrder here if desired
			setNested(output, depData, src, writeTree, morphedTarget[0], morphedTarget[1]);
		});
		return output;
	}

	public ticketHasDependencies(ticketId: string) {
		return (
			(this.predecessorTree.has(ticketId) && this.predecessorTree.get(ticketId).size > 0) ||
			(this.successorTree.has(ticketId) && this.successorTree.get(ticketId).size > 0)
		);
	}

	/**
	 * Calculates an OutputLinks LinkMap based on the passed in ticket data. Expected usage
	 * of manipulating dependencies is to retrieve this map, and pass it into the
	 * other manipulation methods.
	 * @param  tickets    a list of tickets
	 * @return            the OutputLinks LinkMap
	 */
	public getLinks(tickets: ListLike<Ticket>) {
		const mass: Array<LinkMap<DependencyData, string, string>> = [];
		const existanceMap: { [key: string]: Ticket } = {};
		tickets.forEach((t) => {
			mass.push(this.getTicketLinks(t.$id));
			existanceMap[t.$id] = t;
		});
		const links = LinkMap.merge(...mass);

		const ticketCache = new Map<string, Ticket>();

		links.forEachy((src, target, data) => {
			if (existanceMap[src]) {
				links.set(src, target, null);
			}
			if (existanceMap[target]) {
				links.set(src, target, null);
			}

			[this.ticketCache.get(src), this.ticketCache.get(target)].forEach((ticket) => {
				ticketCache.set(ticket.ticket.$id, ticket.ticket);
			});
		});
		return this.convertInternalLinkToOutputLink(links);
	}

	/**
	 * Gets a list of all dependencies connecting a ticket in the current plan to a ticket
	 * in another plan. This is the other manipulation entry point for manipulations.
	 * See also [[getLinks]]
	 */
	public getExternalDependencies() {
		const links: OutputLinks = new LinkMap();

		this.predecessorTree.forEach((val, predId) => {
			const srcTicket = this.checkTicketCache(predId);
			val.forEach((link, succId) => {
				if (
					(srcTicket.isTimelineTicket && !link.ticket.isTimelineTicket) ||
					(link.ticket.isTimelineTicket && !srcTicket.isTimelineTicket)
				) {
					links.set(predId, succId, [srcTicket, link.ticket]);
				}
			});
		});
		return links;
	}

	/**
	 * filter down OutputLinks to only links that connect between the current plan
	 * and other plans.
	 */
	public filterLinksToOnlyExternal(links: OutputLinks, tickets: ListLike<Ticket>) {
		//do this thing incase it's an array
		const existanceMap: { [key: string]: Ticket } = {};
		tickets.forEach((t) => {
			existanceMap[t.$id] = t;
		});

		const newLinks: OutputLinks = new LinkMap();

		links.forEachy((srcId, targetId, tickets) => {
			if (!existanceMap[srcId] || !existanceMap[targetId]) {
				newLinks.set(srcId, targetId, tickets);
			}
		});

		return newLinks;
	}

	/**
	 * Filter [[OutputLinks]] down to only things that do *not* exist in the passed in ticketList
	 * @param  links                  starting point
	 * @param  tickets                List of tickets to be removed
	 * @param  filterPredecessor=true Remove the link if the ticket is one of the predecessors
	 * @param  filterSuccessor=true   Remove the link if the ticket is one of the successors
	 */
	public filterOutLinks(
		links: OutputLinks,
		tickets: ListLike<Ticket>,
		filterPredecessor = true,
		filterSuccessor = true
	) {
		//do this thing incase it's an array
		const existanceMap: { [key: string]: Ticket } = {};
		tickets.forEach((t) => {
			existanceMap[t.$id] = t;
		});

		const newLinks: OutputLinks = new LinkMap();

		links.forEachy((srcId, targetId, tickets) => {
			//this logic feels more readable
			if (existanceMap[srcId] && filterPredecessor) {
			} //skip it
			else if (existanceMap[targetId] && filterSuccessor) {
			} //skip it
			else {
				newLinks.set(srcId, targetId, tickets);
			} //keep it
		});

		return newLinks;
	}

	/**
	 * convert the style of [[LinkMap]] used from the internally used DependencyData
	 * to the externally used OutputLinks
	 */
	public convertInternalLinkToOutputLink(links: LinkMap<any, string, string>) {
		const outputLinks: OutputLinks = new LinkMap();
		links.forEachy((srcId, targetId) => {
			outputLinks.set(srcId, targetId, [
				this.ticketCache.get(srcId).ticket,
				this.ticketCache.get(targetId).ticket,
			]);
		});
		return outputLinks;
	}

	/**
	 * convert the style of [[LinkMap]] used from the externally used OutputLinks
	 * to the internally used DependencyData
	 */
	public convertOutputLinkToInternal(links: LinkMap<any, string, string>) {
		const outputLinks = new LinkMap<DependencyData>();
		links.forEachy((srcId, targetId) => {
			const relationData = this.predecessorTree.get(srcId).get(targetId);
			outputLinks.set(srcId, targetId, relationData.dependencyData);
		});
		return outputLinks;
	}

	/**
	 * (DEPRECATED) Disconnect all the ticket dependency links. This is currently
	 * only in use for ticketDrag
	 * @param  tickets    The tickets to be disconnected.
	 * @param  ignoreList A set of tickets to be excluded from the src side of the link map.
	 * Usecase: disconnect "other" tickets, delete the passed in set after. Provide the
	 * same list of tickets as both arguments. All links pointed _to_ the deleted tickets
	 * will be cleaned up, but none of the things the deleted tickets point to.
	 */
	public unlinkTickets(tickets: ListLike<Ticket>, ignoreList?: ListLike<Ticket>) {
		const mass: Array<LinkMap<DependencyData, string, string>> = [];
		const existanceMap: { [key: string]: Ticket } = {};
		tickets.forEach((t) => {
			mass.push(this.getTicketLinks(t.$id));
			existanceMap[t.$id] = t;
		});
		const links = LinkMap.merge(...mass);

		const ticketCache = new Map<string, Ticket>();

		links.forEachy((src, target, data) => {
			if (existanceMap[src]) {
				links.set(src, target, null);
			}
			if (existanceMap[target]) {
				links.set(src, target, null);
			}

			[this.ticketCache.get(src), this.ticketCache.get(target)].forEach((ticket) => {
				ticketCache.set(ticket.ticket.$id, ticket.ticket);
			});
		});

		if (ignoreList) {
			ignoreList.forEach((t) => {
				links.deleteAll(t.$id);
			});
		}

		return {
			writeData: this.createOutput(links),
			ticketCache: ticketCache,
		};
	}

	public unlinkTicket(ticketId: string, ignoreList?: ListLike<Ticket>) {
		return this.unlinkTickets([this.ticketCache.get(ticketId).ticket]);
	}

	//successor beta bs, can fall back to the better legacy version unlinkTicket after removing that
	// or go right to removing the deprecated ones
	public unlinkTicketLegacy(ticketId: string, ignoreList?) {
		const links = this.getTicketLinks(ticketId);
		//list of tickets with only their literal "ticketId", instead of timelineTicketId
		const ticketCache = new Map<string, Ticket>();

		links.forEachy((src, target, data) => {
			if (src === ticketId) {
				links.set(src, target, null);
			}
			if (target === ticketId) {
				links.set(src, target, null);
			}

			[this.ticketCache.get(src), this.ticketCache.get(target)].forEach((ticket) => {
				ticketCache.set(ticket.ticket.$id, ticket.ticket);
			});
		});

		return {
			writeData: [this.createOutput(links), this.createOutput(links)],
			ticketCache: ticketCache,
		};
	}

	/**
	 * Generate the set of "save ready" output data after setting all the passed in
	 * to either null or something. Essentially mass adding or removing.
	 * TODO - verify the add side. May need to generate a new set of default data
	 * @param  outputLinks The links to set
	 * @param  addOrRemove true to have a link, false to not
	 */
	public massUpdateLinks(outputLinks: OutputLinks, addOrRemove: boolean) {
		const mass: Array<LinkMap<DependencyData>> = [];
		const linksToSet = this.convertOutputLinkToInternal(outputLinks);

		//load in existing data
		linksToSet.forEachy((srcId, targetId) => {
			mass.push(this.getTicketLinks(srcId));
		});
		const links = LinkMap.merge(...mass);
		//apply the update
		linksToSet.forEachy((srcId, targetId, data) => {
			if (addOrRemove) {
				links.set(srcId, targetId, data);
			} else {
				links.set(srcId, targetId, null);
			}
		});

		return {
			writeData: this.createOutput(links),
			outputLinks: this.convertInternalLinkToOutputLink(links),
		};
	}

	private specialIsTimelineTicketCheck(ticket) {
		return (
			ticket.isTimelineTicket || this.timelineTickets.syncList.has(this.currentPlanId + ',' + ticket.$id)
		);
	}

	private decorateTimelineOutput(output, ticket: Ticket, predKey: string) {
		if (!ticket || !this.specialIsTimelineTicketCheck(ticket) || !ticket.view[predKey]) {
			return output;
		}
		if (!output) {
			return ticket.view[predKey];
		}
		return _.merge(ticket.view[predKey], output);
	}
}

function setLineForTree(ticket: Ticket, ticketId: string, tree: DependencyTree) {
	if (tree.has(ticketId)) {
		tree.get(ticketId).forEach((dep, ticketId) => {
			// dep.line = getLineFromTickets(ticket, dep.ticket);
			dep.line = getLineFromTickets(dep.ticket, ticket);

			//timeline ticket shift
			if (dep.ticket.isTimelineTicket) {
				dep.line.start.x = dep.ticket.timelineX;
				dep.line.start.yIsScreen = true;
				dep.line.start.y = 100;
			}
			if (ticket.isTimelineTicket) {
				dep.line.end.x = ticket.timelineX;
				dep.line.end.yIsScreen = true;
				dep.line.end.y = 100;
			}

			if (dep.dependencyData) {
				const data = dep.dependencyData;
				if (data.isCyclic || data.isOutOfOrder) {
					dep.line.color = errorLineColor;
				}
				if (ticket.view.isPlanArchived || dep.ticket.view.isPlanArchived) {
					dep.line.color = greyLineColor;
				}
				if (
					(ticket.view.isPlanArchived && ticket.isTimelineTicket) ||
					(dep.ticket.view.isPlanArchived && dep.ticket.isTimelineTicket)
				) {
					dep.line.text = strings.PROJECT_ARCHIVED_LABEL;
				} else if (data.lagDays > 0) {
					dep.line.text = data.linkageType + '(+' + data.lagDays + 'd lag)';
				} else if (data.lagDays < 0) {
					dep.line.text = data.linkageType + '(' + data.lagDays + 'd lag)';
				} else {
					dep.line.text = data.linkageType;
				}
			}
		});
	}
}

function getLineFromTickets(startTicket: Ticket, endTicket: Ticket) {
	let centeredStartPoint = new Point(
		startTicket.view.left + startTicket.view.width / 2,
		startTicket.view.top + startTicket.view.height / 2
	);
	let centeredEndPoint = new Point(
		endTicket.view.left + endTicket.view.width / 2,
		endTicket.view.top + endTicket.view.height / 2
	);

	if (endTicket.isTimelineTicket) {
		centeredEndPoint.x = endTicket.timelineX;
		centeredEndPoint.y = -10000; //hack'n slash
	}
	if (startTicket.isTimelineTicket) {
		centeredStartPoint.x = startTicket.timelineX;
		centeredStartPoint.y = -10000; //hack'n slash
	}
	// let startOffset = startTicket.view.type === "milestone" ? 10 : 0;
	// let endOffset = endTicket.view.type === "milestone" ? 30 : 10;
	let startOffset = 0;
	let endOffset = 10;

	if (startTicket.view.type === 'milestone') {
		if (startTicket.view.side === Side.PULL) {
			startOffset += MILESTONE_SIZE_OFFSET2.PULL;
		} else {
			startOffset += MILESTONE_SIZE_OFFSET2.ACTIVE;
		}
	}

	if (endTicket.view.type === 'milestone') {
		if (endTicket.view.side === Side.PULL) {
			endOffset += MILESTONE_SIZE_OFFSET2.PULL;
		} else {
			endOffset += MILESTONE_SIZE_OFFSET2.ACTIVE;
		}
	}

	const startAngle = calcAngle(centeredStartPoint, centeredEndPoint);

	centeredStartPoint = getCenterOffsetPoint(
		centeredStartPoint,
		centeredEndPoint,
		startTicket.view.width + startOffset,
		startTicket.view.height + startOffset,
		startTicket.view.type
	);
	centeredEndPoint = getCenterOffsetPoint(
		centeredEndPoint,
		centeredStartPoint,
		endTicket.view.width + endOffset,
		endTicket.view.height + endOffset,
		endTicket.view.type
	);

	const endAngle = calcAngle(centeredStartPoint, centeredEndPoint);
	if (!essentiallyEquals(startAngle, endAngle)) {
		return new Line(centeredEndPoint, centeredStartPoint);
	}

	return new Line(centeredStartPoint, centeredEndPoint);
}

function copyDependencyTree(tree: DependencyTree) {
	const newTree: DependencyTree = new Map();
	tree.forEach((innerMap, key) => {
		newTree.set(key, new Map(innerMap));
	});
	return newTree;
}

function slideDownTree(target: string, tree: DependencyTree, found?: LinkMap) {
	let isCyclical = false;
	if (!found) {
		found = new LinkMap();
	}
	if (!tree.has(target)) {
		return { found, isCyclical: false };
	}
	tree.get(target).forEach((link, subId) => {
		if (found.has(target, subId)) {
			isCyclical = true;
			return;
		}
		found.set(target, subId);
		isCyclical = slideDownTree(subId, tree, found).isCyclical || isCyclical;
	});
	return { found, isCyclical };
}

/**
 * Outputs a set of dependency data derived from the passed in [[OutputLinks]]
 * optimized for display of the data.
 * @param  thing         The links to be displayed, allows null to be passed in.
 * @param  currentPlanId [description]
 * @param  planObject    An object or function that allows getting plan data from
 * an id.
 * @return               [description]
 */
export function genPrettyDependencyList(
	thing: OutputLinks | null,
	currentPlanId: string,
	planObject: { [key: string]: any } | ((string) => any)
) {
	let dependencyOutput: DependencyDisplay[] = [];

	//will always return an object as long as planObject is a function or object
	function plan(key) {
		if (typeof planObject === 'function') {
			return planObject(key) || {};
		} else if (planObject instanceof Map) {
			return planObject.get(key)?.view || {};
		} else {
			return planObject[key] || {};
		}
	}

	if (thing) {
		const list: DependencyDisplay[] = [];

		thing.forEachy((key1, key2, tickets) => {
			const srcPlanId = tickets[1].timelineTicketSplitId
				? tickets[1].timelineTicketSplitId[0]
				: currentPlanId;
			const targetPlanId = tickets[0].timelineTicketSplitId
				? tickets[0].timelineTicketSplitId[0]
				: currentPlanId;
			const obj: DependencyDisplay = {
				srcTicket: tickets[1].database.activityName,
				targetTicket: tickets[0].database.activityName,
				srcPlan: plan(srcPlanId).name || srcPlanId,
				targetPlan: plan(targetPlanId).name || targetPlanId,
			};
			list.push(obj);
		});

		dependencyOutput = list as any;
	}
	return dependencyOutput;
}
